import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import { apiFetchInit, apiOrigin } from "@/lib/apiOrigin";
import { atwAuthSignoutUrl } from "@/lib/atwAuthHost";
import { atwOneAppUrl } from "@/lib/atwOneHost";

export type OneSessionUser = {
  id?: string;
  email?: string | null;
  name?: string | null;
  image?: string | null;
  role?: string | null;
  orgId?: string | null;
  accountDomain?: string | null;
  appEntitlements?: GlobalAppSlug[] | null;
  canManageCustomApps?: boolean;
};

type SessionState = {
  data: { user?: OneSessionUser } | null;
  status: "loading" | "authenticated" | "unauthenticated";
  refresh: () => void;
};

const SessionContext = createContext<SessionState>({
  data: null,
  status: "loading",
  refresh: () => {},
});

async function fetchSession(): Promise<{
  data: { user: OneSessionUser } | null;
  status: "authenticated" | "unauthenticated";
}> {
  const res = await fetch(`${apiOrigin()}/api/auth/session`, {
    credentials: "include",
    cache: "no-store",
    ...apiFetchInit(),
  });
  if (!res.ok) {
    return { data: null, status: "unauthenticated" };
  }
  const data = (await res.json()) as { user?: OneSessionUser };
  if (data?.user?.email) {
    return { data: { user: data.user }, status: "authenticated" };
  }
  return { data: null, status: "unauthenticated" };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<SessionState, "refresh">>({
    data: null,
    status: "loading",
  });
  const inFlight = useRef(false);
  const hasLoadedOnce = useRef(false);

  const loadSession = useCallback(async (opts?: { quiet?: boolean }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const quiet = opts?.quiet === true && hasLoadedOnce.current;
    if (!quiet) {
      setState((s) => ({ ...s, status: "loading" }));
    }
    try {
      const next = await fetchSession();
      hasLoadedOnce.current = true;
      setState(next);
    } catch {
      hasLoadedOnce.current = true;
      setState({ data: null, status: "unauthenticated" });
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  // Quiet re-check when the tab is focused again (cleared cookies / expired session).
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      void loadSession({ quiet: true });
    };
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [loadSession]);

  const refresh = useCallback(() => {
    void loadSession();
  }, [loadSession]);

  return (
    <SessionContext.Provider value={{ ...state, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

export function signOut() {
  void (async () => {
    const origin = apiOrigin();
    try {
      const csrfRes = await fetch(`${origin}/api/auth/csrf`, {
        credentials: "include",
        cache: "no-store",
        ...apiFetchInit(),
      });
      if (csrfRes.ok) {
        const { csrfToken } = (await csrfRes.json()) as { csrfToken?: string };
        if (csrfToken) {
          await fetch(`${origin}/api/auth/signout`, {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              csrfToken,
              callbackUrl: atwOneAppUrl("/"),
            }),
            ...apiFetchInit(),
          });
        }
      }
    } catch {
      /* Auth hub sign-out still runs below */
    }
    window.location.assign(atwAuthSignoutUrl(atwOneAppUrl("/")));
  })();
}
